import { eachMonthOfInterval, format, parseISO } from 'date-fns';
import { formatDate } from '@/utils/date';
import { getMonthCalendarDays, type MonthCalendarDay } from '@/utils/monthCalendar';
import type {
  LeaveReportDates,
  LeaveReportDayKind,
  LeaveReportPeriod,
} from '../types/leaveReport.types';

const MONTH_LABEL_FORMAT = 'MMMM yyyy';

export interface CalendarDay extends MonthCalendarDay {
  isInPeriod: boolean;
}

export interface CalendarMonth {
  key: string;
  label: string;
  days: CalendarDay[];
}

function getLastReportDate(period: LeaveReportPeriod): string {
  const today = formatDate(new Date());
  return period.dateTo < today ? period.dateTo : today;
}

export function getPeriodCalendarMonths(period: LeaveReportPeriod): CalendarMonth[] {
  const lastReportDate = getLastReportDate(period);
  if (lastReportDate < period.dateFrom) return [];

  const isInPeriod = (isoDate: string) => isoDate >= period.dateFrom && isoDate <= lastReportDate;

  return eachMonthOfInterval({
    start: parseISO(period.dateFrom),
    end: parseISO(lastReportDate),
  }).map((monthStart) => ({
    key: formatDate(monthStart),
    label: format(monthStart, MONTH_LABEL_FORMAT),
    days: getMonthCalendarDays(monthStart).map((day) => ({
      ...day,
      isInPeriod: isInPeriod(day.isoDate),
    })),
  }));
}

export function getDayKindByDate(
  dates: LeaveReportDates,
  dayKinds: readonly LeaveReportDayKind[],
): Map<string, LeaveReportDayKind> {
  const dayKindByDate = new Map<string, LeaveReportDayKind>();
  for (const dayKind of dayKinds) {
    for (const isoDate of dates[dayKind]) {
      dayKindByDate.set(isoDate, dayKind);
    }
  }
  return dayKindByDate;
}
